import { useState, useEffect, useMemo } from 'react';
import { Button, CoordioDropdown, Input, Textarea, useStaticDropdownSource } from '@coordio/ui';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { AGENT_DROPDOWN, agentToDropdownOption } from '../agents/agentDropdown';
import type { AgentDefinitionDto } from '../../types/agent';
import type { CreateSessionRequest } from '../../types/session';
import { ParameterForm } from './ParameterForm';
import { agentService } from '../../services/agentService';
import { useContextStore } from '../../store/contextStore';

interface CreateSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSessionRequest) => Promise<void>;
}

/**
 * Dialog component for creating a new agent session.
 */
export const CreateSessionDialog = ({ open, onClose, onSubmit }: CreateSessionDialogProps) => {
  const [sessionName, setSessionName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentDefinitionDto | null>(null);
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [initialMessage, setInitialMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const scopeLabel = currentTenant
    ? currentProject
      ? `Agents for ${currentTenant.name} / ${currentProject.name}`
      : `Agents for ${currentTenant.name} (tenant-level)`
    : 'Agents';

  useEffect(() => {
    if (open) {
      loadAgents();
    } else {
      // Reset form when dialog closes
      setSessionName('');
      setAgentId('');
      setSelectedAgent(null);
      setParameters({});
      setInitialMessage('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!agentId) {
      setSelectedAgent(null);
      setParameters({});
      return;
    }

    let cancelled = false;

    agentService.getAgent(agentId).then((fullAgent) => {
      if (cancelled) return;
      setSelectedAgent(fullAgent);
      const defaultParams: Record<string, any> = {};
      fullAgent.parameters?.forEach((param) => {
        if (!param.isSecret && param.defaultValue) {
          try {
            if (param.type === 'boolean') {
              defaultParams[param.key] = param.defaultValue === 'true';
            } else if (param.type === 'number' || param.type === 'int' || param.type === 'integer') {
              defaultParams[param.key] = Number(param.defaultValue);
            } else if (param.type === 'json' || param.type === 'object' || param.type === 'array') {
              defaultParams[param.key] = param.defaultValue;
            } else {
              defaultParams[param.key] = param.defaultValue;
            }
          } catch {
            defaultParams[param.key] = param.defaultValue;
          }
        }
      });
      setParameters(defaultParams);
    }).catch(() => {
      if (cancelled) return;
      const agent = agents.find((a) => a.id === agentId);
      setSelectedAgent(agent || null);
    });

    return () => { cancelled = true; };
  }, [agentId]);

  const agentOptions = useMemo(() => agents.map(agentToDropdownOption), [agents]);
  const agentSource = useStaticDropdownSource(agentOptions);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const result = await agentService.getAgents({ isActive: true, pageSize: 200 });
      setAgents(result.items);
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!agentId) {
      setError('Please select an agent');
      return;
    }

    // Validate required parameters
    if (selectedAgent) {
      const missingParams = selectedAgent.parameters
        ?.filter((p) => p.isRequired && (!p.isSecret || !p.hasSecretConfigured) && !parameters[p.key])
        .map((p) => p.label || p.key);
      if (missingParams && missingParams.length > 0) {
        setError(`Missing required parameters: ${missingParams.join(', ')}`);
        return;
      }
    }

    setLoading(true);
    try {
      const nonSecretParameters = Object.fromEntries(
        Object.entries(parameters).filter(([key]) => !selectedAgent?.parameters?.some((parameter) => parameter.key === key && parameter.isSecret)),
      );
      const request: CreateSessionRequest = {
        agentDefinitionId: agentId,
        sessionName: sessionName.trim(),
        parameters: Object.keys(nonSecretParameters).length > 0 ? nonSecretParameters : undefined,
        initialMessage: initialMessage.trim() || undefined,
      };
      // When the user does not provide a session name, omit it so the
      // backend defaults it to the new session's identifier.
      if (!request.sessionName) {
        delete (request as Partial<CreateSessionRequest>).sessionName;
      }
      await onSubmit(request);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CortexDialog open={open} onClose={onClose} title="Create New Session" size="lg" data-testid="create-session-dialog">
      <CortexDialogBody>
        <div className="flex flex-col gap-4 pt-2">
          {error && (
            <div className="flex items-center justify-between rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-700 dark:bg-red-950 dark:text-red-300">
              <span>{error}</span>
              <button type="button" onClick={() => setError(null)} className="ml-2 font-medium hover:underline">✕</button>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Session Name (Optional)</label>
            <Input
              className="w-full"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              disabled={loading}
              placeholder="Leave blank to use the session ID as the name"
            />
          </div>

          <span className="text-xs text-muted-foreground mt-1">
            {scopeLabel}
          </span>
          <div>
            <label className="mb-1 block text-sm font-medium">
              Agent <span className="text-red-500">*</span>
            </label>
            <CoordioDropdown
              source={agentSource}
              value={agentId || null}
              onChange={(val) => setAgentId(val ?? '')}
              {...AGENT_DROPDOWN}
              clearable={false}
              disabled={loading || loadingAgents}
              data-testid="create-session-agent-select"
              data-pw="agentDefinitionId"
              data-field-type="select"
            />
            {loadingAgents && (
              <p className="mt-1 text-xs text-muted-foreground">Loading agents…</p>
            )}
          </div>

          {selectedAgent && selectedAgent.parameters && selectedAgent.parameters.length > 0 && (
            <ParameterForm
              parameters={selectedAgent.parameters}
              values={parameters}
              onChange={setParameters}
            />
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Initial Message (Optional)</label>
            <Textarea
              className="w-full"
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              rows={3}
              disabled={loading}
              placeholder="Enter an initial message to start the conversation..."
            />
          </div>
        </div>
      </CortexDialogBody>
      <CortexDialogFooter>
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : 'Create Session'}
        </Button>
      </CortexDialogFooter>
    </CortexDialog>
  );
};
